import { useMsal } from "@azure/msal-react";
import { notifications } from "@mantine/notifications";
import { CHAT_COMPLETIONS_URL } from "../hooks/useChatCompletion";
import { loginRequest } from "../utils/authConfig";
import { AuthRequiredError, acquireAuthToken } from "../utils/fetch";

export const useArtifactHandler = () => {
    const { instance } = useMsal();

    type ResolvedOpenTarget = { kind: "url"; url: string } | { kind: "blob"; url: string };

    const resolveStorageOpenTarget = async (
        storageApiUrl: string,
    ): Promise<ResolvedOpenTarget | null> => {
        try {
            const accessToken = await acquireAuthToken(instance);
            if (!accessToken) {
                throw new AuthRequiredError(storageApiUrl);
            }
            const token = `Bearer ${accessToken}`;

            const res = await fetch(storageApiUrl, {
                method: "GET",
                headers: {
                    Authorization: token,
                    Accept: "*/*",
                },
                redirect: "follow",
            });

            // SUCCESS: File found and accessible, Open as blob
            if (res.ok) {
                const blob = await res.blob();
                const blobUrl = URL.createObjectURL(blob);
                return { kind: "blob", url: blobUrl };
            }

            // AUTH FALLBACK: Only trigger login if it's an auth issue
            if (res.status === 401 || res.status === 403) {
                const isInIframe = window.self !== window.top;
                if (isInIframe) {
                    await instance.loginPopup(loginRequest());
                } else {
                    await instance.loginRedirect(loginRequest());
                }
                return null;
            }

            // GENERIC ERROR: For 500s or other status codes
            notifications.show({
                title: "Error opening file",
                message: `Could not open file (${res.statusText})`,
                color: "red",
            });
            return null;
        } catch (error) {
            console.error(`Error resolving storage file: ${storageApiUrl}`, error);
            notifications.show({
                title: "Error opening file",
                message: "Unexpected error while opening file.",
                color: "red",
            });
            return null;
        }
    };

    // Check if a URL is an Outlook email URL with ImmutableEntryId
    const isOutlookEmailUrl = (url: string): boolean => {
        return url.includes("/v1/search/email?");
    };

    const getFullUrl = (url: string): string => {
        // If it's already a full URL, just clean up any duplicates
        if (url.startsWith("http") || url.startsWith("file://")) {
            // Clean up duplicate v1/storage segments if they exist
            if (url.includes("/v1/storage/v1/storage/")) {
                return url.replace(/\/v1\/storage(\/v1\/storage\/)+/g, "/v1/storage/");
            }
            return url;
        }
        // Check if it's a storage URL pattern that needs the base URL prepended
        if (url.startsWith("v1/storage/")) {
            const fullUrl = `${CHAT_COMPLETIONS_URL}/${url}`;
            // Clean up any duplicate v1/storage segments
            if (fullUrl.includes("/v1/storage/v1/storage/")) {
                return fullUrl.replace(/\/v1\/storage(\/v1\/storage\/)+/g, "/v1/storage/");
            }
            return fullUrl;
        }
        // Handle other relative URLs
        const hrefWithoutSandbox = url
            .replace("sandbox:", "")
            .replace("http://example.com", "")
            .replace("https://example.com", "");

        const decodedHref = decodeURIComponent(hrefWithoutSandbox);
        // Handle special case for storage file URLs
        const finalHref = decodedHref.includes("object_name=/")
            ? decodedHref.replace("object_name=/", "object_name=")
            : decodedHref;

        // Clean up any duplicate v1/storage segments in the final URL
        const fullUrl = `${CHAT_COMPLETIONS_URL}${finalHref.startsWith("/") ? "" : "/"}${finalHref}`;
        if (fullUrl.includes("/v1/storage/v1/storage/")) {
            return fullUrl.replace(/\/v1\/storage(\/v1\/storage\/)+/g, "/v1/storage/");
        }
        return fullUrl;
    };

    const ARTIFACT_EXTENSIONS = [
        ".pdf",
        ".html",
        ".doc",
        ".docx",
        ".ppt",
        ".pptx",
        ".xls",
        ".xlsx",
        ".msg",
        ".csv",
        ".json",
        ".txt",
    ];

    const isArtifactURL = (url?: string): boolean => {
        if (!url) return false;
        // Check if it's a storage URL pattern (e.g., v1/storage/file?object_name=...)
        if (url.startsWith("v1/storage/") || url.includes("/v1/storage/")) {
            return true;
        }

        // Check if it ends with known artifact extensions
        return ARTIFACT_EXTENSIONS.some((extension) => url.toLowerCase().endsWith(extension));
    };

    const getPdfUrl = async (pdfUrl: string): Promise<string | null> => {
        const target = await resolveStorageOpenTarget(pdfUrl);
        return target?.url ?? null;
    };

    const openStorageFile = async (storageApiUrl: string) => {
        const target = await resolveStorageOpenTarget(storageApiUrl);
        if (!target) return;

        const newWindow = window.open(target.url, "_blank", "");

        if (target.kind === "blob") {
            if (newWindow) {
                newWindow.onload = () => URL.revokeObjectURL(target.url);
            } else {
                const a = document.createElement("a");
                a.href = target.url;
                a.target = "_blank";
                a.rel = "noopener noreferrer";
                a.click();
                setTimeout(() => URL.revokeObjectURL(target.url), 10_000);
            }
        }
    };

    // Simple function to open URL without event handling
    const openUrl = async (source: string) => {
        if (!source) return;

        const fullUrl = getFullUrl(source);

        // Handle Outlook email URLs with ImmutableEntryId translation
        if (isOutlookEmailUrl(fullUrl)) {
            const accessToken = await acquireAuthToken(instance);
            if (!accessToken) {
                notifications.show({
                    title: "Error opening email",
                    message: "Please sign in again to open this email.",
                    color: "red",
                });
                return;
            }
            const token = `Bearer ${accessToken}`;
            const response = await fetch(fullUrl, {
                method: "GET",
                headers: {
                    Authorization: token,
                    Accept: "*/*",
                },
            });
            if (response.ok) {
                const data = await response.json();
                window.open(data.webLink, "_blank");
            } else {
                notifications.show({
                    title: "Error opening email",
                    message: `Could not email (${response.statusText})`,
                    color: "red",
                });
            }
            return;
        }

        if (isArtifactURL(source)) {
            if (fullUrl.startsWith("file://")) {
                // For file:// URLs, copy to clipboard
                navigator.clipboard.writeText(fullUrl).then(() => {
                    notifications.show({
                        title: "URL Copied",
                        message:
                            "This is a local file that cannot be opened directly in your browser. Please open a new browser tab and paste the copied URL.",
                        color: "green",
                        autoClose: 4000,
                    });
                });
                return;
            }

            if (fullUrl.startsWith(CHAT_COMPLETIONS_URL)) {
                await openStorageFile(fullUrl);
            } else {
                window.open(fullUrl, "_blank");
            }
        } else {
            window.open(fullUrl, "_blank");
        }
    };

    // Handle artifact clicks with optional disclaimer callback
    const handleArtifactClick = async (
        e: React.MouseEvent<HTMLAnchorElement>,
        source: string,
        disclaimerMessages: string[],
        onShowDisclaimer?: (messages: string[], link: string) => void,
    ) => {
        // Prevent default immediately to stop the browser from following the href
        // This must be called synchronously at the start before any async operations
        e.preventDefault();

        if (!source) return;

        // If there are disclaimer messages and a callback, show disclaimer
        if (disclaimerMessages.length > 0 && onShowDisclaimer) {
            onShowDisclaimer(disclaimerMessages, source);
            return;
        }

        // Otherwise, open the URL directly
        await openUrl(source);
    };

    return {
        getFullUrl,
        isArtifactURL,
        getPdfUrl,
        openStorageFile,
        openUrl,
        handleArtifactClick,
    };
};
